import React, { useEffect, useRef } from 'react';
import type { VideoHTMLAttributes } from 'react';

type HlsVideoProps = VideoHTMLAttributes<HTMLVideoElement> & {
  src?: string;
  forcePause?: boolean;
  isReady?: boolean;
  onVideoSizeChange?: () => void;
};

const HlsVideo = ({
  src,
  forcePause,
  isReady = true,
  onVideoSizeChange,
  ...rest
}: HlsVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !onVideoSizeChange) {
      return;
    }

    videoElement.addEventListener('resize', onVideoSizeChange);
    return () => videoElement.removeEventListener('resize', onVideoSizeChange);
  }, [onVideoSizeChange]);

  useEffect(() => {
    if (!videoRef.current || !src || !isReady) {
      return;
    }

    void import('hls.js').then(({ default: Hls }) => {
      if (!videoRef.current) {
        return;
      }

      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          autoStartLoad: true,
          testBandwidth: true,
          startLevel: -1,
          capLevelToPlayerSize: true,
          lowLatencyMode: true,
          maxStarvationDelay: 0.5,
          maxLoadingDelay: 0.5,
          // Ideally we'd add a solution for https://github.com/video-dev/hls.js/issues/4553
        });
        hls.loadSource(src);
        hls.attachMedia(videoRef.current);
        return () => {
          try {
            hls?.destroy();
          } catch {
            // Ignore errors
          }
        };
      } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
        videoRef.current.src = src;
      }
    });
  }, [isReady, src]);

  useEffect(() => {
    if (forcePause) {
      videoRef.current?.pause();
    } else {
      void videoRef.current?.play();
    }
  }, [forcePause]);

  return <video ref={videoRef} {...rest} playsInline={true} />;
};

export default HlsVideo;
